import { useState } from "react";
import { Box, Button, Flex, Image, Text } from "@chakra-ui/react";
import iconApproved from "../../assets/images/receiveSalary/check-circle.svg";
import iconRejected from "../../assets/images/contract/x-circle.svg";
import { customDateFormatType } from "../../utils/utils";
import toast from "react-hot-toast";
import useDebounce from "../../hooks/useDebounce";
import SearchableSelect from "../../components/selectForm";
import { fetchAllFuel, useFuelRequests } from "../../hooks/useFuelAllowance";
import { fuelAllowanceCall } from "../../api/fuelAllowance";
import { exportExcelFromApi } from "../../utils/tools";
import FuelTabsHeader from "./FuelTabsHeader";
import FuelDateRange from "./FuelDateRange";
import { FuelExportButton, FuelSearchInput } from "./FuelToolbar";
import CheckinRegistrationTable from "../deviceManager/checkinRegistrationTable";
import FuelActionModal from "./FuelActionModal";

// Xăng xe - tab Đơn thay đổi: HCNS duyệt bước 2 (duyệt lúc nào cũng được, có hiệu lực từ lần chấm vào tiếp theo).
// Từ 03/11/2026 đơn cần quản lý duyệt bước 1 trên app trước; chỉ hiện đơn đã qua bước 1 hoặc không cần bước 1 (BE lọc).
const StatusData = [
  { key: "PENDING", value: "Chưa duyệt" },
  { key: "REJECTED", value: "Không được phê duyệt" },
  { key: "APPROVED", value: "Đã được duyệt" },
];

const TypeData = [
  { key: "true", value: "Đăng ký / đổi mức xăng xe" },
  { key: "false", value: "Bỏ đăng ký nhận xăng xe" },
];

const headers = [
  { label: "Trạng thái", key: "status", width: "13%" },
  { label: "Mã NV", key: "code", width: "7%" },
  { label: "Tên NV", key: "name", width: "11%" },
  { label: "Thay đổi", key: "change", width: "21%" },
  { label: "Lý do", key: "reason", width: "12%" },
  { label: "Ngày gửi", key: "createdAt", width: "9%" },
  { label: "Quản lý duyệt", key: "leader", width: "14%" },
  { label: "HCNS xử lý", key: "handler", width: "13%" },
];

const exportMapping = {
  code: "Mã NV",
  name: "Tên NV",
  change: "Thay đổi",
  toValue: "Nhận xăng xe sau khi đổi",
  toLevelLabel: "Mức sau khi đổi",
  alwaysCheckout: "Luôn phải chấm ra",
  reason: "Lý do",
  statusLabel: "Trạng thái",
  createdAt: "Ngày gửi",
  leader: "Quản lý duyệt",
  handler: "HCNS xử lý",
  handledAt: "Ngày xử lý",
};

const TableFuelRequests = ({ tab, setTab }) => {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [toValue, setToValue] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const debouncedSearch = useDebounce(search, 500);

  const filters = {
    search: debouncedSearch,
    status,
    toValue,
    fromDate: dateRange[0] ? customDateFormatType(dateRange[0]) : "",
    toDate: dateRange[1] ? customDateFormatType(dateRange[1]) : "",
  };

  const {
    items,
    refetch,
    isLoading,
    isRefetching,
    totalPages,
    setCurrentPage,
    currentPage,
    entries,
    startEntry,
    endEntry,
  } = useFuelRequests(filters);

  // Đơn đang mở popup duyệt / không duyệt: { row, status }
  const [handling, setHandling] = useState(null);
  const submitStatus = (nextStatus, row) => setHandling({ row, status: nextStatus });

  const submitHandle = async (adminNote) => {
    const { row, status: nextStatus } = handling;
    try {
      await fuelAllowanceCall.handleRequest(row.id, {
        status: nextStatus,
        ...(adminNote ? { adminNote } : {}),
      });
      await refetch();
      toast.success("Cập nhập trạng thái thành công");
      setHandling(null);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const isApprove = handling?.status === "APPROVED";

  const handleExport = () =>
    exportExcelFromApi({
      fetcher: () => fetchAllFuel("requests", filters, exportMapping),
      mapping: exportMapping,
      filename: "don_thay_doi_xang_xe.xlsx",
    });

  const renderCell = (key, row) => {
    if (key !== "status") return <Text>{row[key]}</Text>;

    if (row.status === "APPROVED") {
      return (
        <Flex gap={"8px"} w={"max-content"}>
          <Image src={iconApproved} />
          <Text color={"var(--color-status-employ)"}>Đã được duyệt</Text>
        </Flex>
      );
    }
    if (row.status === "REJECTED") {
      return (
        <Flex gap={"8px"} w={"max-content"}>
          <Image src={iconRejected} />
          <Text color={"var(--text-red-employee)"}>Không được phê duyệt</Text>
        </Flex>
      );
    }
    if (row.status !== "PENDING") return <Text>-</Text>;

    return (
      <Flex gap={"8px"} w={"max-content"}>
        <Button
          bg={"var(--linear-gradient-employee)"}
          color={"var(--color-main)"}
          fontSize={"12px"}
          fontWeight={"600"}
          _hover={{ filter: "brightness(90%)" }}
          onClick={() => submitStatus("APPROVED", row)}
          h={"28px"}
          minH={"28px"}
          w={"90px"}
        >
          Duyệt
        </Button>
        <Button
          bg={"var(--color-main)"}
          color={"var(--color-option-employee-hover)"}
          border={"1px solid var(--color-option-employee-hover)"}
          fontSize={"12px"}
          fontWeight={"600"}
          _hover={{ filter: "brightness(90%)" }}
          onClick={() => submitStatus("REJECTED", row)}
          h={"28px"}
          minH={"28px"}
          w={"90px"}
        >
          Ko duyệt
        </Button>
      </Flex>
    );
  };

  return (
    <>
      <FuelTabsHeader
        tab={tab}
        setTab={setTab}
        onRefresh={() => refetch()}
        rightSlot={<FuelExportButton onClick={handleExport} />}
      />

      <Box mt={{ base: "23px" }}>
        <Flex
          backgroundColor="var(--color-main)"
          borderRadius={{ base: "12px" }}
          flexDirection={"column"}
        >
          <Flex
            p={"16px 16px 12px 16px"}
            gap={{ base: "16px 32px" }}
            alignItems={"center"}
            flexWrap={"wrap"}
          >
            <FuelSearchInput
              onChange={(v) => {
                setSearch(v);
                setCurrentPage(1);
              }}
            />
            <SearchableSelect
              isShowSearch={false}
              options={StatusData}
              initialSelectedOption={{ key: 0, value: "Chọn trạng thái" }}
              setValue={(v) => {
                setStatus(v || "");
                setCurrentPage(1);
              }}
            />
            <SearchableSelect
              isShowSearch={false}
              options={TypeData}
              initialSelectedOption={{ key: 0, value: "Chọn loại đơn" }}
              setValue={(v) => {
                setToValue(v || "");
                setCurrentPage(1);
              }}
            />
            <FuelDateRange
              value={dateRange}
              placeholder="Ngày gửi từ ngày - đến ngày"
              onChange={(r) => {
                setDateRange(r);
                setCurrentPage(1);
              }}
            />
          </Flex>

          <CheckinRegistrationTable
            headers={headers}
            rows={items}
            renderCell={renderCell}
            isLoaded={!isLoading && !isRefetching}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(p) => setCurrentPage(p.selected + 1)}
            startEntry={startEntry}
            endEntry={endEntry}
            entries={entries}
          />
        </Flex>
      </Box>

      <FuelActionModal
        isOpen={!!handling}
        onClose={() => setHandling(null)}
        title={isApprove ? "Duyệt đơn thay đổi xăng xe" : "Không duyệt đơn thay đổi xăng xe"}
        fields={
          handling
            ? [
                {
                  label: "Nhân viên",
                  value: `${handling.row.code} - ${handling.row.name}`,
                },
                { label: "Thay đổi", value: handling.row.change },
                { label: "Lý do của nhân viên", value: handling.row.reason },
                { label: "Quản lý duyệt", value: handling.row.leader },
              ]
            : []
        }
        noteLabel={isApprove ? "Ghi chú" : "Lý do không duyệt"}
        notePlaceholder={
          isApprove
            ? "Nhập ghi chú (không bắt buộc)"
            : "Nhập lý do không duyệt (không bắt buộc)"
        }
        message={
          isApprove
            ? `${
                handling?.row?.toValue
                  ? "Từ lần chấm vào tiếp theo nhân viên phải chấm ra mới đủ công."
                  : handling?.row?.alwaysCheckout
                    ? "Nhân viên thuộc Khối hỗ trợ kinh doanh nên vẫn phải chấm ra mới đủ công."
                    : "Từ lần chấm vào tiếp theo nhân viên không cần chấm ra."
              }`
            : "Nhân viên giữ nguyên loại hiện tại và nhận được thông báo kèm lý do."
        }
        submitText={isApprove ? "Duyệt" : "Không duyệt"}
        onSubmit={submitHandle}
      />
    </>
  );
};

export default TableFuelRequests;
